import { c as _c } from "react/compiler-runtime";
import { getOrgArtifactsByArtifactId } from '@onefootprint/fetch-dashboard';
import type { OrgArtifact } from '@onefootprint/request-types/dashboard';
import { useQueries } from '@tanstack/react-query';

// Judgment values are arbitrary JSON; only scalars make a legible sidebar tag. Objects/arrays/null
// get no tag — their detail lives in the full JudgementView.
export const formatJudgmentValueTag = (value: unknown): string | undefined => {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim() || undefined;
  return undefined;
};
const fetchJudgmentValue = async (artifactId: string): Promise<string | null> => {
  const response = await getOrgArtifactsByArtifactId({
    path: {
      artifactId
    },
    parseAs: 'blob',
    throwOnError: true
  });
  const text = await (response.data as Blob).text();
  try {
    const parsed = JSON.parse(text) as {
      value?: unknown;
    };
    // react-query forbids `undefined` as query data, so no-tag cases resolve to null.
    return formatJudgmentValueTag(parsed?.value) ?? null;
  } catch {
    return null;
  }
};

// Judgments, rule results, and decision results keep their value inside the artifact's (encrypted)
// content, not its metadata, so the sidebar can't show it without reading the bytes. Fans out one
// content fetch per such artifact and returns { artifactId: value } for the scalar-valued ones.
// `enabled` gates on manual_review, since the content endpoint 403s otherwise.
const useJudgmentValues = t0 => {
  const $ = _c(2);
  const {
    artifacts,
    enabled
  } = t0;
  const judgmentArtifacts = artifacts.filter(_temp);
  let t1;
  if ($[0] !== enabled) {
    t1 = artifact => ({
      queryKey: ["judgment-value", artifact.id] as const,
      enabled,
      queryFn: () => fetchJudgmentValue(artifact.id)
    });
    $[0] = enabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const results = useQueries({
    queries: judgmentArtifacts.map(t1)
  });
  const values = {};
  judgmentArtifacts.forEach((artifact_0, i) => {
    const value = results[i]?.data;
    if (value) {
      values[artifact_0.id] = value;
    }
  });
  return values;
};
export default useJudgmentValues;
function _temp(a) {
  return a.metadata.kind === "agent_graph_judgement" || a.metadata.kind === "agent_graph_rule_result" || a.metadata.kind === "agent_graph_decision_result";
}